'use client';

import { useCallback, useMemo, useRef, useState } from 'react';

import { AlertTriangle, Check, Copy, Link2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import type { InteractionProgress } from '../hooks/useInteractionCheck';
import type {
  ChplQuote,
  DrugInteraction,
  InteractionCheckResult,
  NoInteraction,
  ViewChplOptions,
} from '../types';
import { getPairKey } from '../types';
import { formatInteractionsAsText } from '../utils/format-interactions-text';
import type { Drug } from './drug-search';
import { InteractionCard } from './interaction-card';
import { InteractionPharmacologicalCommentary } from './interaction-pharmacological-commentary';
import { InteractionsSummaryCard } from './interactions-summary-card';
import { NoInteractionsBanner } from './no-interactions-banner';
import { SafePairsSection } from './safe-pairs-section';

interface InteractionResultsProps {
  drugs: Drug[];
  /** Drugs that were skipped from analysis (no ChPL extraction) */
  skippedDrugs?: Drug[];
  /** Interaction results from API */
  results?: InteractionCheckResult[] | null;
  isLoading?: boolean;
  /** Loading progress message (reserved for future use) */
  _loadingMessage?: string;
  /** Progress stats for the loading indicator */
  progress?: InteractionProgress | null;
  onViewChpl?: (quote: ChplQuote, options?: ViewChplOptions) => void;
  /** Shareable URL for copying */
  shareableUrl?: string;
  /** Controls which sections to render: 'full' (default), 'title-only', or 'cards-only' */
  renderMode?: 'full' | 'title-only' | 'cards-only';
}

/**
 * Main component for displaying drug interaction analysis results
 */
export function InteractionResults({
  drugs,
  skippedDrugs = [],
  results,
  isLoading = false,
  _loadingMessage,
  progress,
  onViewChpl,
  shareableUrl,
  renderMode = 'full',
}: InteractionResultsProps) {
  const { t } = useTranslation('interactions');
  const [textCopied, setTextCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const urlCopyTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleCopyUrl = useCallback(async () => {
    if (!shareableUrl) return;
    try {
      await navigator.clipboard.writeText(shareableUrl);
    } catch {
      const input = document.createElement('input');
      input.value = shareableUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setUrlCopied(true);
    clearTimeout(urlCopyTimeoutRef.current);
    urlCopyTimeoutRef.current = setTimeout(() => setUrlCopied(false), 2000);
  }, [shareableUrl]);

  const handleCopyText = useCallback(async () => {
    if (!results) return;
    const text = formatInteractionsAsText(drugs, results, t);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setTextCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setTextCopied(false), 2000);
  }, [drugs, results, t]);

  // Build analysis summary from results
  const analysisResults = useMemo(() => {
    if (!results || results.length === 0) return null;

    let criticalCount = 0;
    let importantCount = 0;
    let moderateCount = 0;
    let minorCount = 0;

    for (const r of results) {
      if (r.hasInteraction) {
        switch (r.severity) {
          case 'critical':
            criticalCount++;
            break;
          case 'important':
            importantCount++;
            break;
          case 'moderate':
            moderateCount++;
            break;
          case 'minor':
            minorCount++;
            break;
        }
      }
    }

    return {
      drugs,
      results,
      summary: {
        totalPairs: results.length,
        interactionsFound:
          criticalCount + importantCount + moderateCount + minorCount,
        criticalCount,
        importantCount,
        moderateCount,
        minorCount,
      },
    };
  }, [drugs, results]);

  // Show message if not enough drugs with ChPL extraction but some were skipped
  if (drugs.length < 2 && skippedDrugs.length > 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 dark:border-amber-500 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <span className="font-medium text-amber-800 dark:text-amber-200">
              {t('results.cannotAnalyze')}
            </span>
            <p className="mt-1 text-amber-700 dark:text-amber-300">
              {t('results.skippedDrugs', { count: skippedDrugs.length })}{' '}
              {t('results.noDataInSystem')}{' '}
              {skippedDrugs.map((d) => d.productName).join(', ')}
            </p>
            <p className="mt-1 text-amber-700 dark:text-amber-300">
              {t('results.addAtLeastTwo')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (drugs.length < 2) {
    return null;
  }

  if (isLoading) {
    const pairsCount = progress?.total ?? 0;
    const drugsCount = drugs.length;

    return (
      <div className="rounded-lg border-2 border-zinc-200 bg-zinc-50 p-8 dark:border-zinc-700 dark:bg-zinc-800">
        <div className="flex items-center justify-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
          <span className="text-zinc-600 dark:text-zinc-400">
            {t('check.analyzing', {
              drugs: t('drugsCount', { count: drugsCount }),
              pairs: t('pairsCount', { count: pairsCount }),
            })}
          </span>
        </div>
        {/* Progress bar */}
        {progress && progress.total > 0 && (
          <div className="mt-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
              <div
                className="h-full rounded-full bg-zinc-500 transition-all duration-500 ease-out dark:bg-zinc-400"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!analysisResults) {
    return null;
  }

  // Separate interactions from safe pairs
  const interactions = analysisResults.results.filter(
    (r): r is DrugInteraction => r.hasInteraction,
  );
  const safePairs = analysisResults.results.filter(
    (r): r is NoInteraction => !r.hasInteraction,
  );

  // Sort interactions by severity (critical first)
  const severityOrder = { critical: 0, important: 1, moderate: 2, minor: 3 };
  const sortedInteractions = [...interactions].sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity],
  );

  // Scroll to a specific interaction card
  const handlePairClick = (interaction: DrugInteraction) => {
    const pairKey = getPairKey(interaction.pair);
    const element = document.getElementById(`interaction-${pairKey}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Brief highlight effect
      element.classList.add('ring-2', 'ring-offset-2', 'ring-zinc-400');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-offset-2', 'ring-zinc-400');
      }, 1500);
    }
  };

  // No interactions at all — show the prominent green banner
  const hasNoInteractions = sortedInteractions.length === 0;

  if (renderMode === 'title-only') {
    return (
      <div className="space-y-2">
        <InteractionsSummaryCard
          summary={analysisResults.summary}
          drugCount={drugs.length}
          shareableUrl={shareableUrl}
          titleOnly
          onCopyText={handleCopyText}
          textCopied={textCopied}
        />
      </div>
    );
  }

  if (renderMode === 'cards-only') {
    return (
      <div className="space-y-8">
        {sortedInteractions.map((interaction, index) => (
          <InteractionCard
            key={index}
            id={`interaction-${getPairKey(interaction.pair)}`}
            interaction={interaction}
          />
        ))}

        <SafePairsSection safePairs={safePairs} />

        <InteractionPharmacologicalCommentary
          interactions={sortedInteractions}
          safePairs={safePairs}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Skipped drugs notice */}
      {skippedDrugs.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 p-4 dark:border-amber-500 dark:bg-amber-950/30">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-sm">
            <span className="font-medium text-amber-800 dark:text-amber-200">
              {t('results.skippedInAnalysis', { count: skippedDrugs.length })}
            </span>
            <span className="text-amber-700 dark:text-amber-300">
              {' '}
              {t('results.noDataInSystem')}{' '}
            </span>
            <span className="text-amber-700 dark:text-amber-300">
              {skippedDrugs.map((d) => d.productName).join(', ')}
            </span>
          </div>
        </div>
      )}

      {hasNoInteractions ? (
        /* Big green "no interactions" banner */
        <>
          <NoInteractionsBanner
            safePairs={safePairs}
            drugCount={drugs.length}
            totalPairs={analysisResults.summary.totalPairs}
            shareableUrl={shareableUrl}
            onViewChpl={onViewChpl}
            onCopyText={handleCopyText}
            textCopied={textCopied}
          />

          <InteractionPharmacologicalCommentary
            interactions={sortedInteractions}
            safePairs={safePairs}
          />
        </>
      ) : (
        <>
          {/* Summary card with overview and legend */}
          <InteractionsSummaryCard
            summary={analysisResults.summary}
            drugCount={drugs.length}
            topInteractions={sortedInteractions}
            onPairClick={handlePairClick}
          />

          {/* Copy actions + interaction cards */}
          <div className="mt-6 space-y-6 sm:mt-12">
            <div className="mb-4 flex flex-col items-center gap-1 sm:flex-row sm:justify-center sm:gap-4">
              {shareableUrl && (
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase transition-colors',
                    urlCopied
                      ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                      : 'text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-400',
                  )}
                >
                  {urlCopied ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Link2 className="h-3 w-3" />
                  )}
                  {urlCopied ? t('copied') : t('results.copyLink')}
                </button>
              )}
              {shareableUrl && (
                <span className="hidden h-3 w-px bg-zinc-200 sm:inline-block dark:bg-zinc-700" />
              )}
              <button
                type="button"
                onClick={handleCopyText}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[10px] font-semibold tracking-wider uppercase transition-colors',
                  textCopied
                    ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-400',
                )}
              >
                {textCopied ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {textCopied ? t('copied') : t('results.copyResults')}
              </button>
            </div>
            {sortedInteractions.map((interaction, index) => (
              <InteractionCard
                key={index}
                id={`interaction-${getPairKey(interaction.pair)}`}
                interaction={interaction}
                onViewChpl={onViewChpl}
              />
            ))}
          </div>

          {/* Safe pairs (collapsed by default) */}
          <SafePairsSection safePairs={safePairs} />

          <InteractionPharmacologicalCommentary
            interactions={sortedInteractions}
            safePairs={safePairs}
          />
        </>
      )}
    </div>
  );
}
